/*
  Clinic tracking (snippets/tracking-head.liquid loads it, deferred).

  Built to the agreed proposal, .assistant/work/pabau-tracking/tracking-map.html
  sections 2 to 5, 8 and 9, and REQUIREMENTS, Clinic / booking, "Booking
  journeys and tracking, 2 Oct". Event names and parameters are section 3's
  list; the names the live site already sends are kept, so GA4 reports carry on.

  One helper, window.DDJTrack.track(event, params):
    - always pushes { event, ...params } to the dataLayer, where GTM picks it up;
    - while Theme settings, Clinic booking, "Send conversions directly" is on,
      ALSO sends it straight to GA4 with gtag, unless the published GTM
      container already routes it (only pabau_form_submitted, today), which
      would count it twice. The live-named events carry live's own
      parameters (event_category, event_label, destination_url) as well.

  Also here:
    - consent updates from Shopify's Customer Privacy API;
    - first and last touch (utm_*, gclid, gbraid, wbraid, fbclid, landing page,
      referrer), 90 days, in localStorage;
    - the `dataLayerDetails` hand-off the live site replays for GTM's
      pabau_form_submitted tag (written by assets/pabau-form.js only after
      the server says Pabau accepted the lead);
    - one click listener: [data-track] elements, phone and email links
      (contact_click), and every link to the Pabau booking portal (campaign
      tags added, pabau_online_booking_visit sent with a beacon, the route
      kept for the visit);
    - pabau_online_booking on /pages/pabau-online-thank-you, once per visit,
      only with ?booking=online, with Meta's Schedule alongside it;
    - sendMeta(), which holds every Meta event until the pixel is set up;
    - the Calendly listener, with live's event names;
    - [data-track-on-load] markers (contact_form_submitted).

  Never sends a conversion on page load except pabau_online_booking (and its
  Schedule) above.
*/
(function () {
  if (window.DDJTrack) return;

  var cfg = window.DDJTracking || {};
  var GA4 = cfg.ga4 || 'G-DBJHSMJ9YS';
  var ctx = cfg.context || {};
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }

  /* Routed by the published GTM container (GTM-MRQS9CT, read 25 Sep): a
   * direct send as well would double it. */
  var ROUTED_BY_GTM = { pabau_form_submitted: true };

  /* Live's own parameters for the names it already sends. */
  var LIVE_PARAMS = {
    pabau_online_booking_visit: { event_category: 'conversion' },
    pabau_online_booking: { event_category: 'conversion' },
    calendly_embed_viewed: { event_category: 'engagement', event_label: 'Calendly Embed Loaded' },
    calendly_event_type_viewed: { event_category: 'engagement', event_label: 'Calendly Event Type Viewed' },
    calendly_slot_selected: { event_category: 'engagement', event_label: 'Calendly Slot Selected' },
    calendly_booking_complete: { event_category: 'engagement', event_label: 'Calendly Booking Complete' }
  };

  /* Meta custom events live fires itself (the pixel comes from the Shopify
   * Facebook & Instagram app; fbq is only used when the page has it). */
  var META_CUSTOM = {
    pabau_form_submitted: 'pabau_booking_complete',
    calendly_booking_complete: 'calendly_booking_complete'
  };

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k) && src[k] !== undefined) target[k] = src[k];
    }
    return target;
  }

  function clean(params) {
    var out = {};
    for (var k in params) {
      if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
      var v = params[k];
      if (v === undefined || v === null || v === '') continue;
      out[k] = v;
    }
    return out;
  }

  /* ─── Meta, once the pixel is set up ─────────────────────────────────── */

  /* The pixel comes from the Shopify Facebook & Instagram app and appears a
   * moment after this deferred file runs (measured live 21 Oct: about 100 ms
   * later), so a one-time check for fbq loses the race and Meta gets nothing.
   * sendMeta waits until fbq exists AND has a pixel set up, trying every
   * 250 ms for up to 10 s, then gives up quietly (visitors who decline
   * marketing cookies never get the pixel). No stand-in fbq is made: Meta's
   * loader skips loading when fbq already exists. */
  var META_RETRY_MS = 250;
  var META_GIVE_UP_MS = 10000;

  function metaReady() {
    if (typeof window.fbq !== 'function') return false;
    try {
      var state = typeof window.fbq.getState === 'function' ? window.fbq.getState() : null;
      return !!(state && state.pixels && state.pixels.length);
    } catch (e) { return false; }
  }

  function sendMeta(kind, name, eventId) {
    var started = Date.now();
    (function attempt() {
      if (metaReady()) {
        try {
          if (eventId) window.fbq(kind, name, {}, { eventID: String(eventId) });
          else window.fbq(kind, name);
        } catch (e) { /* never break the page */ }
        return;
      }
      if (Date.now() - started >= META_GIVE_UP_MS) return;
      setTimeout(attempt, META_RETRY_MS);
    })();
  }

  /**
   * track(event, params, options)
   *   options.beacon  send the GA4 hit with transport_type beacon, for a
   *                   click that leaves the page
   *   options.direct  false to skip the direct GA4 send for this call
   */
  function track(event, params, options) {
    params = clean(params || {});
    options = options || {};
    window.dataLayer.push(assign({ event: event }, params));

    if (cfg.direct && options.direct !== false && !ROUTED_BY_GTM[event]) {
      var ga = assign({ send_to: GA4 }, LIVE_PARAMS[event], params);
      if (options.beacon) ga.transport_type = 'beacon';
      try { gtag('event', event, ga); } catch (e) { /* never break the page */ }
    }

    if (cfg.direct && META_CUSTOM[event]) sendMeta('trackCustom', META_CUSTOM[event], params.event_id);
  }

  /* ─── Consent, from Shopify's Customer Privacy API ──────────────────── */

  function applyConsent() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return;
    var analytics = !!cp.analyticsProcessingAllowed();
    var marketing = typeof cp.marketingAllowed === 'function' ? !!cp.marketingAllowed() : analytics;
    gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied'
    });
  }

  document.addEventListener('visitorConsentCollected', applyConsent);
  function loadPrivacyApi() {
    if (window.Shopify && window.Shopify.customerPrivacy) { applyConsent(); return; }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) applyConsent();
      });
    }
  }

  /* ─── Storage helpers ────────────────────────────────────────────────── */

  /* Site storage is never assumed. With it blocked (Safari's Block All
   * Cookies, some in-app browsers) merely reading window.localStorage throws
   * SecurityError, and that once killed the enquiry submit after its
   * preventDefault: no POST, no message. Every read and write goes through
   * store(), which answers null instead of throwing. */
  function store(name) {
    try { return window[name] || null; } catch (e) { return null; }
  }
  function readJSON(name, key) {
    var s = store(name);
    if (!s) return null;
    try { var raw = s.getItem(key); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  }
  function writeJSON(name, key, value) {
    var s = store(name);
    if (!s) return;
    try { s.setItem(key, JSON.stringify(value)); } catch (e) { /* blocked */ }
  }

  /* ─── First and last touch ───────────────────────────────────────────── */

  var TOUCH_DAYS = 90;
  var CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'gbraid', 'wbraid', 'fbclid'];

  function fresh(touch) {
    return touch && touch.at && (Date.now() - touch.at) < TOUCH_DAYS * 864e5;
  }

  function captureTouch() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var touch = {};
    var hasCampaign = false;
    CAMPAIGN_KEYS.forEach(function (k) {
      var v = params.get(k);
      if (v) { touch[k] = v.slice(0, 300); hasCampaign = true; }
    });

    var referrer = '';
    try {
      if (document.referrer) {
        var r = new URL(document.referrer);
        if (r.hostname !== window.location.hostname) referrer = document.referrer.slice(0, 500);
      }
    } catch (e) { /* ignore */ }

    touch.landing_page = (window.location.pathname + window.location.search).slice(0, 500);
    touch.referrer = referrer;
    touch.at = Date.now();

    var first = readJSON('localStorage', 'ddj_first_touch');
    if (!fresh(first)) writeJSON('localStorage', 'ddj_first_touch', touch);

    /* Last touch moves on a new campaign, or on a new arrival from another
     * site; plain internal browsing never overwrites it. */
    var last = readJSON('localStorage', 'ddj_last_touch');
    if (hasCampaign || referrer || !fresh(last)) writeJSON('localStorage', 'ddj_last_touch', touch);
  }

  /* The touch a lead is credited to: the last one, else the first. */
  function attribution() {
    var last = readJSON('localStorage', 'ddj_last_touch');
    var first = readJSON('localStorage', 'ddj_first_touch');
    var t = fresh(last) ? last : (fresh(first) ? first : null);
    if (!t) return {};
    var out = {};
    CAMPAIGN_KEYS.concat(['landing_page', 'referrer']).forEach(function (k) { if (t[k]) out[k] = t[k]; });
    return out;
  }

  function userId() {
    if (window.pabauUserId) return window.pabauUserId;
    var id = '';
    var ls = store('localStorage');
    if (ls) try { id = ls.getItem('pabau_user_id') || ''; } catch (e) { /* ignore */ }
    if (!id) {
      for (var j = 0; j < 10; j++) id += '0123456789abcdefghijklmnopqrstuvwxyz'.charAt(Math.floor(Math.random() * 36));
    }
    window.pabauUserId = id;
    return id;
  }

  function attributionSubmit() {
    var id = userId();
    if (window.AttributionTracker && typeof window.AttributionTracker.submit === 'function') {
      try {
        var p = window.AttributionTracker.submit({ unique_id: id });
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } catch (e) { /* ignore */ }
    }
  }

  /* ─── The enquiry hand-off, as live replays it ───────────────────────── */

  /* Live writes `dataLayerDetails` and the next page pushes it for GTM's
   * pabau_form_submitted tag. 2.0 writes it (only once the server says Pabau
   * accepted the lead, and only while direct sends are on) with the same key
   * and shape, plus the new parameters in `ddj_enquiry_extra`, merged into the
   * one push so the conversion still counts once. */
  function replayHandOff() {
    var details = readJSON('localStorage', 'dataLayerDetails');
    if (!details) return;
    var extra = readJSON('localStorage', 'ddj_enquiry_extra') || {};
    var ls = store('localStorage');
    if (ls) try {
      ls.removeItem('dataLayerDetails');
      ls.removeItem('ddj_enquiry_extra');
    } catch (e) { /* ignore */ }
    window.dataLayer.push(assign({}, details, clean(extra)));
    if (cfg.direct) sendMeta('trackCustom', 'pabau_booking_complete', extra.event_id);
  }

  /* ─── Pabau portal links ─────────────────────────────────────────────── */

  function isPabauBooking(href) {
    return /(^|\.)pabau\.com\//i.test(href.replace(/^https?:\/\//i, '')) && /online-bookings/i.test(href);
  }

  /* Campaign tags carried to the portal: the visitor's own (last touch), so
   * a session that Pabau's tags restart keeps its source. Google's linker
   * (_gl) is added by the Google tag itself on the click, from the linker
   * domains set in tracking-head; it is kept if already on the link. */
  function tagPabauUrl(href) {
    var url;
    try { url = new URL(href, window.location.href); } catch (e) { return href; }
    var a = attribution();
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
      if (a[k] && !url.searchParams.has(k)) url.searchParams.set(k, a[k]);
    });
    return url.toString();
  }

  /**
   * Record a click out to the portal. Called for every Pabau portal link,
   * overlay or not. `info` = { route, treatment, clinic, cta_location }.
   */
  function bookingVisit(href, info) {
    info = info || {};
    var route = info.route || 'clinic_treatment';
    var bookingType = route === 'clinic_consultation' ? 'consultation' : 'treatment';
    var visit = {
      route: route,
      booking_type: bookingType,
      treatment: info.treatment || '',
      clinic: info.clinic || '',
      at: Date.now()
    };
    writeJSON('sessionStorage', 'ddj_booking_route', visit);
    writeJSON('localStorage', 'ddj_booking_route', visit);

    track('pabau_online_booking_visit', {
      route: route,
      treatment: info.treatment,
      clinic: info.clinic,
      cta_location: info.cta_location,
      destination: href,
      destination_url: href
    }, { beacon: true });

    attributionSubmit();
  }

  /* ─── Clicks ─────────────────────────────────────────────────────────── */

  function datasetParams(el) {
    var out = {};
    for (var i = 0; i < el.attributes.length; i++) {
      var attr = el.attributes[i];
      if (attr.name.indexOf('data-track-') === 0 && attr.name !== 'data-track-on-load') {
        out[attr.name.slice(11).replace(/-/g, '_')] = attr.value;
      }
    }
    return out;
  }

  function pageClinic() {
    return ctx.clinic ? ctx.clinic.value : '';
  }

  function onClick(event) {
    if (event.defaultPrevented) return;
    var target = event.target;
    if (!target || !target.closest) return;

    var tracked = target.closest('[data-track]');
    if (tracked) track(tracked.getAttribute('data-track'), datasetParams(tracked));

    var link = target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href') || '';

    if (/^tel:/i.test(href) || /^mailto:/i.test(href)) {
      track('contact_click', {
        method: /^tel:/i.test(href) ? 'phone' : 'email',
        clinic: link.getAttribute('data-clinic') || pageClinic(),
        link_url: href.replace(/^(tel|mailto):/i, '')
      });
      return;
    }

    if (isPabauBooking(link.href)) {
      var tagged = tagPabauUrl(link.href);
      if (tagged !== link.href) link.href = tagged;
      bookingVisit(link.href, {
        route: link.getAttribute('data-booking-route') || 'clinic_treatment',
        treatment: link.getAttribute('data-booking-treatment') || (ctx.treatment ? ctx.treatment.handle : ''),
        clinic: link.getAttribute('data-booking-clinic') || pageClinic(),
        cta_location: link.getAttribute('data-booking-location') || 'page'
      });
    }
  }

  /* ─── Booking thank-you page ─────────────────────────────────────────── */

  function bookingThankYou() {
    if (!ctx.page || ctx.page.handle !== 'pabau-online-thank-you') return;
    attributionSubmit();
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (params.get('booking') !== 'online') return;
    var ss = store('sessionStorage');
    if (!ss) return; /* no session storage: no way to guard a refresh, so count nothing */
    /* One id per visit for Meta's Schedule, kept with the guard. */
    var scheduleId = 'ddj-schedule-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    try {
      if (ss.getItem('ddj_pabau_booking_counted')) return;
      ss.setItem('ddj_pabau_booking_counted', String(Date.now()));
      ss.setItem('ddj_pabau_schedule_id', scheduleId);
    } catch (e) {
      return; /* no session storage: no way to guard a refresh, so count nothing */
    }
    var visit = readJSON('sessionStorage', 'ddj_booking_route');
    if (!visit) {
      var stored = readJSON('localStorage', 'ddj_booking_route');
      if (stored && stored.at && Date.now() - stored.at < 864e5) visit = stored;
    }
    visit = visit || {};
    track('pabau_online_booking', {
      booking_type: visit.booking_type,
      route: visit.route,
      treatment: visit.treatment,
      clinic: visit.clinic
    });
    /* Meta's Schedule means a real booking only when it is sent from here:
     * Pabau's own fires when the portal opens, before anything is booked. */
    if (cfg.direct) sendMeta('track', 'Schedule', scheduleId);
  }

  /* ─── Calendly ───────────────────────────────────────────────────────── */

  var CALENDLY = {
    'calendly.profile_page_viewed': 'calendly_embed_viewed',
    'calendly.event_type_viewed': 'calendly_event_type_viewed',
    'calendly.date_and_time_selected': 'calendly_slot_selected',
    'calendly.event_scheduled': 'calendly_booking_complete'
  };
  var calendlyFired = {};

  window.addEventListener('message', function (e) {
    if (!e.origin || e.origin.indexOf('calendly.com') === -1 || !e.data || !e.data.event) return;
    var name = CALENDLY[e.data.event];
    if (!name || calendlyFired[name]) return;
    calendlyFired[name] = true;
    track(name, {});
  });

  /* ─── On-load markers ────────────────────────────────────────────────── */

  function onLoadMarkers() {
    document.querySelectorAll('[data-track-on-load]').forEach(function (el) {
      var name = el.getAttribute('data-track-on-load');
      var key = 'ddj_onload_' + name + '_' + window.location.pathname + window.location.search;
      var ss = store('sessionStorage');
      if (ss) try {
        if (ss.getItem(key)) return;
        ss.setItem(key, '1');
      } catch (e) { /* count it anyway */ }
      track(name, datasetParams(el));
    });
  }

  /* ─── Start ──────────────────────────────────────────────────────────── */

  window.DDJTrack = {
    track: track,
    attribution: attribution,
    userId: userId,
    attributionSubmit: attributionSubmit,
    bookingVisit: bookingVisit,
    tagPabauUrl: tagPabauUrl,
    isPabauBooking: isPabauBooking,
    context: ctx
  };

  try { captureTouch(); } catch (e) { /* ignore */ }
  document.addEventListener('click', onClick);

  function ready() {
    loadPrivacyApi();
    replayHandOff();
    bookingThankYou();
    onLoadMarkers();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
})();
